import {
  formatErrorWithRequestId,
  getRequestTransportErrorMessage,
  getSafeServerErrorMessage,
  readApiErrorEnvelope,
} from '#/utils/requestError';
import {
  getExtraItemRemainingReferenceQty,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';

function formatBlockerLine(item) {
  if (!item || typeof item !== 'object') {
    return String(item ?? '');
  }
  const type = item.type || 'BLOCKER';
  const parts = [type];
  if (item.itemName) {
    parts.push(item.itemName);
  }
  if (item.requiredQty != null || item.deliveredQty != null || item.remainingQty != null) {
    parts.push(
      `需 ${item.requiredQty ?? '—'}／已 ${item.deliveredQty ?? '—'}／剩 ${item.remainingQty ?? '—'}`,
    );
  }
  if (item.referenceQty != null) {
    parts.push(
      `參考 ${item.referenceQty}／已交 ${item.deliveredQty ?? '—'}／剩 ${item.remainingQty ?? '—'}`,
    );
  }
  return parts.filter(Boolean).join(' · ');
}

export function getCustomerOrderCloseBlockedDetails(error) {
  const data = error?.response?.data;
  if (!data || data.status !== 409) {
    return null;
  }
  if (data.code && data.code !== 'CUSTOMER_ORDER_CLOSE_BLOCKED') {
    // 仍接受同類 409 details
    if (!data.details?.blockers && !data.details?.warnings) {
      return null;
    }
  }
  const blockers = Array.isArray(data.details?.blockers) ? data.details.blockers : [];
  const warnings = Array.isArray(data.details?.warnings) ? data.details.warnings : [];
  if (blockers.length === 0 && warnings.length === 0 && data.code !== 'CUSTOMER_ORDER_CLOSE_BLOCKED') {
    return null;
  }
  return {
    code: data.code || 'CUSTOMER_ORDER_CLOSE_BLOCKED',
    message: data.message || '客戶訂單尚未符合結單條件',
    blockers,
    warnings,
  };
}

export function formatCustomerOrderCloseBlockedMessage(details) {
  if (!details) {
    return '';
  }
  const lines = [details.message];
  if (details.blockers?.length) {
    lines.push('阻擋：');
    details.blockers.forEach((item) => {
      lines.push(`· ${formatBlockerLine(item)}`);
    });
  }
  if (details.warnings?.length) {
    lines.push('警示：');
    details.warnings.forEach((item) => {
      lines.push(`· ${formatBlockerLine(item)}`);
    });
  }
  return lines.join('\n');
}

export function getCustomerOrderApiErrorMessage(error, fallback = '操作失敗') {
  const transportMessage = getRequestTransportErrorMessage(error);
  if (transportMessage) {
    return transportMessage;
  }

  const blocked = getCustomerOrderCloseBlockedDetails(error);
  if (blocked) {
    return formatCustomerOrderCloseBlockedMessage(blocked);
  }

  const data = error?.response?.data;
  const code = data?.code;

  if (code === 'CUSTOMER_ORDER_SNAPSHOT_UNAVAILABLE') {
    const envelope = readApiErrorEnvelope(error);
    const message = envelope?.message || '客戶訂單資料處理失敗';
    return formatErrorWithRequestId(message, envelope?.requestId);
  }

  if (code === 'RESOURCE_VERSION_CONFLICT') {
    return data?.message || '資料已被他人修改，請重新載入後再試';
  }

  if (code === 'CUSTOMER_ORDER_NOT_FOUND' || code === 'RESOURCE_NOT_FOUND') {
    return data?.message || '找不到客戶訂單資料';
  }

  if (
    code === 'CUSTOMER_ORDER_SCHEDULE_IN_USE'
    || code === 'SCHEDULE_IN_USE'
    || code === 'CUSTOMER_ORDER_SCHEDULE_REFERENCED'
  ) {
    return data?.message || '此排程已有銷貨單引用，無法刪除';
  }

  if (code === 'CUSTOMER_ORDER_MANUAL_LINE_REFERENCED_BY_SALES_ORDER') {
    return data?.message || '已被銷貨單引用的其他產品不可刪除，請重新載入後保留該列';
  }

  if (code === 'SALES_ORDER_QTY_EXCEEDS_ORDER') {
    return data?.message || '銷貨數量超過訂單剩餘量';
  }

  const serverMessage = getSafeServerErrorMessage(error, fallback);
  if (serverMessage) {
    return serverMessage;
  }

  if (data?.message) {
    return data.message;
  }

  if (code) {
    return `${fallback}（${code}）`;
  }

  return fallback;
}

export function isCustomerOrderVersionConflict(error) {
  return error?.response?.data?.code === 'RESOURCE_VERSION_CONFLICT';
}

/** detail 上是否有配件未交完（舊 close acknowledge；現契約不擋關單）。 */
export function hasCustomerOrderExtraItemShortage(detail) {
  return (detail?.lines ?? []).some((line) => (
    (line.extraItems ?? []).some((item) => (
      Number(getExtraItemRemainingReferenceQty(item)) > 0
    ))
  ));
}
